import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2, ArrowLeft } from "lucide-react";
import { Button } from "@tellescope/ui";
import { useTellescopeQuery, useSecureFileUrl } from "@tellescope/tanstack-query";
import { ContentViewer } from "./ContentViewer";

/**
 * Legacy content view page that handles direct content URLs.
 * Maps to old portal's /content/:id route.
 *
 * URL params:
 * - id: the content record ID
 */
export const ContentViewPage = () => {
  const { t } = useTranslation("content");
  const { id } = useParams<{ id: string }>();

  // Fetch content record using TanStack Query
  const {
    data: content,
    isLoading: isLoadingContent,
    error: fetchError,
  } = useTellescopeQuery({
    model: "managed_content_records",
    method: "getOne",
    request: id ?? "",
    enabled: !!id,
  });

  // Extract secureName from content (only for PDF/Video, not Article)
  const secureName = content?.type !== "Article" ? content?.attachments?.[0]?.secureName : undefined;
  const attachmentUrl = useSecureFileUrl(secureName);

  // useSecureFileUrl loading/error is transparent - URL is undefined until loaded
  const isLoading = isLoadingContent || (secureName && !attachmentUrl);
  const error = fetchError?.message ?? null;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center text-muted-foreground">
          <Loader2 className="h-8 w-8 mx-auto mb-2 animate-spin" />
          <p className="text-sm">{t("Loading content...")}</p>
        </div>
      </div>
    );
  }

  if (error || !content) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <p className="text-sm font-medium text-destructive mb-1">{t("Unable to load content")}</p>
          <p className="text-xs text-muted-foreground mb-4">{error ?? t("Content not found")}</p>
          <Link to="/content">
            <Button variant="outline">
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t("Go Back")}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 max-w-4xl">
      {/* Header with back button */}
      <div className="flex items-center gap-3 mb-6">
        <Link to="/content">
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </Link>
        <h1 className="text-2xl font-bold truncate">{content.title}</h1>
      </div>

      {/* Content */}
      <div className="bg-background rounded-lg border overflow-hidden">
        <ContentViewer content={content} attachmentUrl={attachmentUrl ?? null} />
      </div>
    </div>
  );
};
